import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { patch } from '@ngxs/store/operators';
import { EngagementsService } from '@pages/talent-finder/services/engagements.service';
import {
  CloseEngagement,
  CreateEngagement,
  CreateEngagementCompleted,
  DeleteEngagement,
  EditEngagement,
  LoadEngagement,
  LoadEngagementLookups,
  LoadEngagements,
  LoadHasAnyEngagements,
} from '@pages/talent-finder/store/actions/engagements.actions';
import { EngagementLookup, EngagementLookups } from '@pages/talent-finder/models/request/engagement-lookups.interface';
import { EngagementItem } from '@pages/talent-finder/models/engagements/engagement-item.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { EngagementsResponse } from '@pages/talent-finder/models/engagements/engagements-response.interface';
import { IEngagementPreview } from '@pages/talent-finder/models/engagement-preview.interface';
import { nonFlickerLoader } from '@kaseya-utils';

interface EngagementsStateModel {
  engagements?: EngagementItem[];
  totalCount?: number;
  hasAnyEngagements?: boolean;
  engagementsLookups?: EngagementLookups;
  engagementsLookupsLoading: boolean;
  createLoading: boolean;
  editLoading: boolean;
  hasAnyEngagementsLoading: boolean;
  hasAnyEngagementsError: boolean;
  engagement?: IEngagementPreview;
  isEngagementLoading: boolean;
  loading: boolean;
  error: boolean;
}

@State<EngagementsStateModel>({
  name: 'engagements',
  defaults: {
    createLoading: false,
    editLoading: false,
    engagementsLookupsLoading: false,
    hasAnyEngagementsLoading: false,
    hasAnyEngagementsError: false,
    isEngagementLoading: false,
    loading: false,
    error: false,
  },
})
@Injectable()
export class EngagementsState {
  private readonly engagementsService = inject(EngagementsService);

  @Selector()
  static engagements(state: EngagementsStateModel): EngagementItem[] | undefined {
    return state.engagements;
  }

  @Selector()
  static totalCount(state: EngagementsStateModel): number | undefined {
    return state.totalCount;
  }

  @Selector()
  static hasAnyEngagements(state: EngagementsStateModel): boolean | undefined {
    return state.hasAnyEngagements;
  }

  @Selector()
  static educationLevels(state: EngagementsStateModel): EngagementLookup[] {
    return state.engagementsLookups?.educationLevels || [];
  }

  @Selector()
  static experienceLevels(state: EngagementsStateModel): EngagementLookup[] {
    return state.engagementsLookups?.experienceLevels || [];
  }

  @Selector()
  static jobTypes(state: EngagementsStateModel): EngagementLookup[] {
    return state.engagementsLookups?.jobTypes || [];
  }

  @Selector()
  static kaseyaCertifications(state: EngagementsStateModel): EngagementLookup[] {
    return state.engagementsLookups?.kaseyaCertifications || [];
  }

  @Selector()
  static industryCertifications(state: EngagementsStateModel): EngagementLookup[] {
    return state.engagementsLookups?.industryCertifications || [];
  }

  @Selector()
  static engagementsLookupsLoading(state: EngagementsStateModel): boolean {
    return state.engagementsLookupsLoading;
  }

  @Selector()
  static createLoading(state: EngagementsStateModel): boolean {
    return state.createLoading;
  }

  @Selector()
  static editLoading(state: EngagementsStateModel): boolean {
    return state.createLoading;
  }

  @Selector()
  static hasAnyEngagementsLoading(state: EngagementsStateModel): boolean {
    return state.hasAnyEngagementsLoading;
  }

  @Selector()
  static loading(state: EngagementsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static hasAnyEngagementsError(state: EngagementsStateModel): boolean {
    return state.hasAnyEngagementsError;
  }

  @Selector()
  static engagement(state: EngagementsStateModel): IEngagementPreview | undefined {
    return state.engagement;
  }

  @Selector()
  static isEngagementLoading(state: EngagementsStateModel): boolean {
    return state.isEngagementLoading;
  }

  @Selector()
  static error(state: EngagementsStateModel): boolean {
    return state.error;
  }

  @Action(CreateEngagement)
  createEngagement(context: StateContext<EngagementsStateModel>, { engagement }: CreateEngagement): Observable<void> {
    context.setState(patch<EngagementsStateModel>({ createLoading: true }));

    return this.engagementsService.createEngagement(engagement).pipe(
      finalize(() => context.setState(patch<EngagementsStateModel>({ createLoading: false }))),
      tap(() => context.dispatch(new CreateEngagementCompleted()))
    );
  }

  @Action(EditEngagement)
  editEngagement(context: StateContext<EngagementsStateModel>, { id, engagement }: EditEngagement): Observable<void> {
    context.setState(patch<EngagementsStateModel>({ editLoading: true }));

    return this.engagementsService
      .editEngagement(id, engagement)
      .pipe(finalize(() => context.setState(patch<EngagementsStateModel>({ editLoading: false }))));
  }

  @Action(LoadHasAnyEngagements)
  loadHasAnyEngagements(context: StateContext<EngagementsStateModel>): Observable<boolean | undefined> {
    return nonFlickerLoader(this.engagementsService.getHasAnyEngagements(), 500).pipe(
      tap(hasAnyEngagements => {
        if (hasAnyEngagements === undefined) {
          context.setState(patch<EngagementsStateModel>({ hasAnyEngagementsLoading: true }));
          return;
        }

        context.setState(
          patch<EngagementsStateModel>({
            hasAnyEngagements,
            hasAnyEngagementsLoading: false,
            hasAnyEngagementsError: false,
          })
        );
      }),
      catchError(() => {
        context.setState(
          patch<EngagementsStateModel>({
            hasAnyEngagementsLoading: false,
            hasAnyEngagementsError: true,
          })
        );
        return EMPTY;
      })
    );
  }

  @Action(LoadEngagementLookups)
  loadEngagementLookups(context: StateContext<EngagementsStateModel>): Observable<EngagementLookups | undefined> {
    return nonFlickerLoader(this.engagementsService.getEngagementLookups(), 1000).pipe(
      tap(engagementsLookups => {
        if (engagementsLookups === undefined) {
          context.setState(patch<EngagementsStateModel>({ engagementsLookupsLoading: true }));
          return;
        }

        context.setState(patch<EngagementsStateModel>({ engagementsLookups, engagementsLookupsLoading: false }));
      })
    );
  }

  @Action(LoadEngagements)
  loadEngagements(
    context: StateContext<EngagementsStateModel>,
    { engagementsFilterRequest }: LoadEngagements
  ): Observable<EngagementsResponse> {
    context.setState(patch<EngagementsStateModel>({ loading: true }));

    return this.engagementsService.getEngagements(engagementsFilterRequest).pipe(
      finalize(() => context.setState(patch<EngagementsStateModel>({ loading: false }))),
      tap(({ items, totalCount }) =>
        context.setState(
          patch<EngagementsStateModel>({
            engagements: items,
            totalCount,
            error: false,
          })
        )
      ),
      catchError((errorResponse: HttpErrorResponse) => {
        context.setState(patch<EngagementsStateModel>({ engagements: [], totalCount: 0, error: true }));
        return throwError(() => errorResponse);
      })
    );
  }

  @Action(LoadEngagement)
  loadEngagement(context: StateContext<EngagementsStateModel>, { id }: LoadEngagement): Observable<IEngagementPreview | undefined> {
    context.setState(patch<EngagementsStateModel>({ engagement: undefined, isEngagementLoading: true }));
    return nonFlickerLoader(this.engagementsService.getEngagement(id), 500).pipe(
      tap(engagement => {
        if (engagement === undefined) {
          context.setState(patch<EngagementsStateModel>({ isEngagementLoading: true }));
          return;
        }

        context.setState(patch<EngagementsStateModel>({ engagement: { ...engagement, id } }));
      }),
      finalize(() => {
        context.setState(patch<EngagementsStateModel>({ isEngagementLoading: false }));
      })
    );
  }

  @Action(CloseEngagement)
  closeEngagement(context: StateContext<EngagementsStateModel>, { id }: CloseEngagement): Observable<void> {
    return this.engagementsService.closeEngagement(id);
  }

  @Action(DeleteEngagement)
  deleteEngagement(context: StateContext<EngagementsStateModel>, { id }: DeleteEngagement): Observable<void> {
    return this.engagementsService.deleteEngagement(id);
  }
}
